import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, shareReplay } from 'rxjs';
import { AuthService } from '../services/auth.service';

let refreshInFlight$: ReturnType<AuthService['refreshAccessToken']> | null = null;

/**
 * Retries failed requests once after a successful token refresh.
 * CPNotes: Single-flight refresh to avoid concurrent rotate races
 */
export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || shouldSkipRefresh(req.url)) {
        return throwError(() => error);
      }

      if (!authService.refreshTokenValue) {
        authService.logout();
        return throwError(() => error);
      }

      if (!refreshInFlight$) {
        refreshInFlight$ = authService.refreshAccessToken().pipe(
          shareReplay(1)
        );
      }

      return refreshInFlight$.pipe(
        switchMap(accessToken => {
          refreshInFlight$ = null;
          const cloned = req.clone({
            setHeaders: { Authorization: `Bearer ${accessToken}` }
          });
          return next(cloned);
        }),
        catchError(refreshError => {
          refreshInFlight$ = null;
          authService.logout();
          return throwError(() => refreshError);
        })
      );
    })
  );
};

function shouldSkipRefresh(url: string): boolean {
  const normalized = url.toLowerCase();
  return normalized.includes('auth/token')
    || normalized.includes('auth/refresh')
    || normalized.includes('auth/logout');
}
